import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, ExpressCheckoutElement, useStripe, useElements } from "@stripe/react-stripe-js";
import PhoneInput from "react-phone-number-input";
import { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import ar from "react-phone-number-input/locale/ar.json";
import "react-phone-number-input/style.css";
import { api, extractError } from "@/lib/api";
import { detectFunnel } from "@/lib/funnels";
import {
  pushDataLayer,
  buildPlanItem,
  buildCustomer,
  savePendingPurchase,
  paymentIntentIdFromSecret,
} from "@/lib/gtm";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Loader2, CheckCircle2, ArrowLeft, ArrowRight, Tag, Shield } from "lucide-react";
import { SHOW_HOME_WORKOUT } from "../config/featureFlags";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK);

// ─── Country → local currency mapping ──────────────────────────
// Every country here shows its own price. Anything else falls back to USD.
const COUNTRY_TO_CURRENCY = {
  SA: "SAR",
  AE: "AED",
  QA: "QAR",
  KW: "KWD",
  OM: "OMR",
  BH: "BHD",  // Bahrain — very close to SAR (1 BHD ≈ 9.95 SAR)
};

// Exchange rates relative to 1 SAR. Kept intentionally in-app (no live FX call)
// so the checkout page stays fast and the price is deterministic.
// Values derived from the existing config equivalents (SAR 397 → local amount).
const SAR_TO = {
  SAR: 1,
  AED: 0.98,     // 397 → 389
  QAR: 0.97,     // 397 → 385
  KWD: 0.0806,   // 397 → 32
  OMR: 0.1033,   // 397 → 41
  BHD: 0.1005,
  USD: 0.267,    // 397 → 106
};

const CURRENCY_LABEL = {
  SAR: "ريال",
  AED: "درهم",
  QAR: "ريال قطري",
  KWD: "دينار",
  OMR: "ريال عماني",
  BHD: "دينار بحريني",
  USD: "USD",
};

function convertFromSAR(amountSAR, currency) {
  const rate = SAR_TO[currency];
  if (!rate) return null;
  const converted = amountSAR * rate;
  // Kuwait, Oman, Bahrain dinars usually rounded to whole units (small unit values)
  if (["KWD", "OMR", "BHD"].includes(currency)) return Math.round(converted);
  return Math.round(converted);
}

// Stripe cannot charge these currencies from our US-based account — visitor
// still SEES the price in their local currency, but the actual charge goes
// through in SAR. Everything else charges natively.
const STRIPE_CHARGEABLE = new Set(["SAR", "AED", "QAR", "USD"]);
// Only maps timezones we care about; anything else falls through.
const TZ_TO_COUNTRY = {
  "Asia/Riyadh": "SA",
  "Asia/Dubai": "AE",
  "Asia/Qatar": "QA",
  "Asia/Kuwait": "KW",
  "Asia/Muscat": "OM",
  "Asia/Bahrain": "BH",
};

function timezoneCountry() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TZ_TO_COUNTRY[tz] || null;
  } catch { return null; }
}

export default function CheckoutPage() {
  // Renewal mode — set when the SubscriptionExpiredPage sends the user here.
  // In this mode we (a) preselect their previous plan, (b) skip the contact
  // step because the user is already logged in, and (c) show a renewal
  // header instead of the generic marketing hero.
  const initialQuery = useMemo(() => new URLSearchParams(window.location.search), []);
  const isRenewal = initialQuery.get("renewal") === "1";
  const initialPlanFromUrl = (() => {
    const p = (initialQuery.get("plan") || "").toLowerCase();
    return ["1mo", "6mo", "12mo", "lifetime"].includes(p) ? p : null;
  })();

  const [step, setStep] = useState(1);
  const [contact, setContact] = useState({ full_name: "", email: "", whatsapp: "" });
  const [defaultCountry, setDefaultCountry] = useState("SA");
  const [leadId, setLeadId] = useState(null);
  const [plan, setPlan] = useState(initialPlanFromUrl || "12mo");
  const [couponCode, setCouponCode] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [config, setConfig] = useState(null);
  const [plansData, setPlansData] = useState(null); // { plans: [{ id, sar_amount, pricing: {SAR:.., AED:..}, ... }] }
  const [creating, setCreating] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [chargeInfo, setChargeInfo] = useState(null); // { amount_local, currency, display_amount, display_currency, sub_interval, sub_interval_count }
  // Autofill: returning visitors are pre-filled from localStorage so they don't
  // re-type their info on subsequent visits.
  const [prefilled, setPrefilled] = useState(false);
  const [attributionBanner, setAttributionBanner] = useState(null); // {channel, step, coupon}
  // Renewal-mode: expired-subscription context (name + email + whatsapp fetched
  // straight from /auth/me and /subscription/renewal-context). We keep this in
  // a separate state so we can render a personalised header while the normal
  // contact flow stays untouched for new customers.
  const [renewalCtx, setRenewalCtx] = useState(null);

  useEffect(() => {
    api.get("/checkout/config").then((r) => setConfig(r.data)).catch(() => {});
    // Marketing plans (source of truth for on-screen prices — admin-editable)
    api.get("/checkout/plans").then((r) => setPlansData(r.data)).catch(() => {});
  }, []);

  // ── Renewal mode ──────────────────────────────────────────────────────
  // When `?renewal=1` is present the visitor is already logged in and has
  // arrived from `SubscriptionExpiredPage`. We fetch their identity + the
  // renewal context, prefill the contact fields, create a fresh lead in
  // the background (so `/checkout/create` will have a valid `lead_id`),
  // and jump straight to step 2 (plan + payment) so they can complete
  // the payment in seconds.
  useEffect(() => {
    if (!isRenewal) return;
    let alive = true;
    (async () => {
      try {
        const [meRes, ctxRes] = await Promise.allSettled([
          api.get("/auth/me"),
          api.get("/subscription/renewal-context"),
        ]);
        if (!alive) return;
        if (ctxRes.status === "fulfilled") {
          setRenewalCtx(ctxRes.value.data || null);
        }
        if (meRes.status !== "fulfilled") return;
        const u = meRes.value.data || {};
        const fill = {
          full_name: u.full_name || "",
          email: u.email || "",
          whatsapp: u.phone || "",
        };
        setContact(fill);
        setPrefilled(true);
        // Create a "renewal" lead in the background so /checkout/create
        // has the `lead_id` it requires. Silent — we don't block the UI
        // waiting for it. If it fails, the user can retry by editing the
        // contact form via the "تعديل البيانات" link.
        if (fill.full_name && fill.email && fill.whatsapp) {
          api.post("/checkout/lead", {
            ...fill,
            source_funnel: "renewal",
          })
            .then((r) => { if (alive && r.data?.lead_id) setLeadId(r.data.lead_id); })
            .catch(() => { /* silent — user can retry from step 1 */ });
        }
        setStep(2);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [isRenewal]);

  // Marketing-attribution prefill: if URL has ?prefill_token=xxx we fetch the
  // lead info tied to that token and auto-fill step-1. Runs FIRST so it wins
  // over the localStorage/cookie prefill for cross-device recovery.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("prefill_token");
    if (!token) return;
    api.get(`/attribution/prefill/${token}`).then((r) => {
      const s = r.data;
      if (!s || !s.ok) return;
      setContact((c) => ({
        full_name: s.full_name || c.full_name,
        email: s.email || c.email,
        whatsapp: s.whatsapp || c.whatsapp,
      }));
      setPrefilled(true);
      if (s.coupon_code) setCouponCode(s.coupon_code);
      setAttributionBanner({
        channel: s.channel, step: s.step, coupon: s.coupon_code,
      });
    }).catch(() => {});
  }, []);

  // Restore last-known contact info: 1) localStorage (fast, works offline)
  // 2) server /checkout/prefill (recognises the same browser via cookies / IP+UA hash)
  useEffect(() => {
    let filled = false;
    try {
      const raw = localStorage.getItem("ff_checkout_prefill");
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && (saved.full_name || saved.email || saved.whatsapp)) {
          setContact((c) => ({
            full_name: c.full_name || saved.full_name || "",
            email: c.email || saved.email || "",
            whatsapp: c.whatsapp || saved.whatsapp || "",
          }));
          setPrefilled(true);
          filled = true;
        }
      }
    } catch {}
    // If localStorage was empty (e.g. new device / cleared cache), ask the server.
    if (!filled) {
      api.get("/checkout/prefill").then((r) => {
        const s = r.data;
        if (s && (s.full_name || s.email || s.whatsapp)) {
          setContact((c) => ({
            full_name: c.full_name || s.full_name || "",
            email: c.email || s.email || "",
            whatsapp: c.whatsapp || s.whatsapp || "",
          }));
          setPrefilled(true);
        }
      }).catch(() => {});
    }
  }, []);

  // Detect visitor's country: 1) IP geolocation, 2) timezone hint, 3) browser locale, 4) SA default
  useEffect(() => {
    // Helper: extract country code from browser locale (e.g. "ar-SA" -> "SA", "en-GB" -> "GB")
    const localeCountry = () => {
      const langs = [navigator.language, ...(navigator.languages || [])];
      for (const l of langs) {
        if (!l) continue;
        const m = l.match(/[-_]([A-Z]{2})\b/i);
        if (m) return m[1].toUpperCase();
      }
      return null;
    };

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 3000); // 3s timeout

    const finalize = (cc) => {
      // Priority chain: IP result → timezone → locale → keep SA default
      if (cc && /^[A-Z]{2}$/i.test(cc)) { setDefaultCountry(cc.toUpperCase()); return; }
      const tz = timezoneCountry();
      if (tz) { setDefaultCountry(tz); return; }
      const lc = localeCountry();
      if (lc) setDefaultCountry(lc);
    };

    fetch("https://ipapi.co/country/", { signal: controller.signal })
      .then((r) => (r.ok ? r.text() : null))
      .then((cc) => { clearTimeout(timer); finalize((cc || "").trim()); })
      .catch(() => { clearTimeout(timer); finalize(null); });

    return () => { clearTimeout(timer); controller.abort(); };
  }, []);

  // Country derived from phone number if the visitor already typed one — overrides IP/timezone.
  // Falls back to the auto-detected defaultCountry so the currency badge always has a value.
  const phoneCountry = useMemo(() => {
    if (!contact.whatsapp) return null;
    try {
      const parsed = parsePhoneNumber(contact.whatsapp);
      return parsed?.country || null;
    } catch { return null; }
  }, [contact.whatsapp]);

  const effectiveCountry = phoneCountry || defaultCountry || "SA";
  const displayCurrency = COUNTRY_TO_CURRENCY[effectiveCountry] || "USD";
  const showLocalPrice = displayCurrency !== "SAR";
  // KWD / OMR / BHD are shown to the visitor but charged in SAR
  // (Stripe US accounts don't chargeback these currencies).
  const chargedInSAR = showLocalPrice && !STRIPE_CHARGEABLE.has(displayCurrency);

  const proceedToStep2 = async (e) => {
    e.preventDefault();
    if (!contact.whatsapp || !isValidPhoneNumber(contact.whatsapp)) {
      toast.error("رقم الجوال غير صحيح"); return;
    }
    setSavingLead(true);
    try {
      // Attach the marketing funnel that captured this visitor (flex / homeworkout / …)
      // so backend can persist it on the lead + purchase + user + route
      // funnel-specific email/WhatsApp sequences.
      const source_funnel = detectFunnel();
      const { data } = await api.post("/checkout/lead", { ...contact, source_funnel });
      setLeadId(data.lead_id);
      const normalized = { ...contact, whatsapp: data.whatsapp };
      setContact(normalized); // show normalized
      // Persist for future visits — enables one-tap checkout for returning customers.
      try { localStorage.setItem("ff_checkout_prefill", JSON.stringify(normalized)); } catch {}
      setStep(2);
    } catch (e) { toast.error(extractError(e, "تعذر حفظ البيانات")); }
    finally { setSavingLead(false); }
  };

  const validateCoupon = async () => {
    if (!couponCode) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/checkout/validate-coupon", { code: couponCode, plan, plan_variant: plan });
      setCouponInfo(data);
      // Toast shows savings in the visitor's display currency (matches on-screen prices).
      let savings = data.original_sar - data.final_sar;
      let currencyLabel = "SAR";
      if (displayCurrency !== "SAR") {
        const origEq = (data.equivalents?.original || []).find((e) => (e.code || "").toUpperCase() === displayCurrency);
        const finalEq = (data.equivalents?.final || []).find((e) => (e.code || "").toUpperCase() === displayCurrency);
        if (origEq && finalEq) {
          savings = Math.max(0, Number(origEq.amount) - Number(finalEq.amount));
          currencyLabel = displayCurrency;
        }
      }
      if (savings > 0) {
        toast.success(`تم تطبيق الخصم — وفّرت ${savings} ${currencyLabel}`);
      } else {
        setCouponInfo(null);
        toast.error("هذا الكود لا يطبق أي خصم فعّال");
      }
    } catch (e) {
      setCouponInfo(null);
      toast.error(extractError(e, "رمز الخصم غير صالح"));
    } finally { setCouponBusy(false); }
  };

  const startPayment = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/checkout/create", {
        lead_id: leadId,
        plan: "subscription",         // API-level plan type
        plan_variant: plan,           // "6mo" or "12mo" — locks in the on-screen price
        coupon_code: couponCode || null,
        currency: displayCurrency,    // Phase 2: charge in buyer's local currency
      });
      setClientSecret(data.client_secret);
      setChargeInfo({
        amount_local: data.amount_local,
        currency: data.currency,
        display_amount: data.display_amount,
        display_currency: data.display_currency,
        sub_interval: data.sub_interval,
        sub_interval_count: data.sub_interval_count,
        mode: data.mode,
      });

      // ── GA4 begin_checkout_ff ─────────────────────────────────
      // Amount actually shown to the buyer (already reflects the picked variant
      // and any applied coupon on the display side).
      const funnel = detectFunnel();
      const value = Number(data.display_amount ?? data.amount_local ?? 0);
      const currency = data.display_currency || data.currency || displayCurrency;
      const item = buildPlanItem({ funnel, plan_variant: plan, price: value, currency });
      pushDataLayer("begin_checkout_ff", {
        ecommerce: {
          currency,
          value,
          coupon: couponInfo?.code || couponCode || undefined,
          items: [item],
        },
        funnel,
        customer: buildCustomer(contact),
      });

      // Persist a snapshot for the success page — Stripe 3DS redirects wipe React state.
      savePendingPurchase({
        transaction_id: paymentIntentIdFromSecret(data.client_secret),
        funnel,
        plan_variant: plan,
        currency,
        value,
        coupon: couponInfo?.code || couponCode || null,
        item,
        contact,
        lead_id: leadId,
      });
    } catch (e) { toast.error(extractError(e, "تعذر بدء الدفع")); }
    finally { setCreating(false); }
  };

  useEffect(() => { setCouponInfo(null); }, [plan]);

  if (!config) return <div className="min-h-screen grid place-items-center bg-[#0A0A0A]"><Loader2 className="h-8 w-8 animate-spin" style={{ color: "#FF6A00" }} /></div>;

  return (
    <div dir="rtl" className="min-h-screen bg-[#0A0A0A] text-white font-[system-ui,'IBM_Plex_Sans_Arabic','Tajawal',sans-serif] py-10 px-4 relative overflow-hidden">
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full blur-[130px] opacity-20 pointer-events-none" style={{ background: "#FF6A00" }} aria-hidden />
      <div className="absolute -bottom-40 -right-40 w-[420px] h-[420px] rounded-full blur-[130px] opacity-15 pointer-events-none" style={{ background: "#FF6A00" }} aria-hidden />

      <div className="max-w-2xl mx-auto space-y-6 relative z-10">
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-1 text-[11px] md:text-xs font-semibold text-white/70">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#FF6A00" }} />
            اشتراك واحد يفتح كل شي
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white">
            اشترك الآن
          </h1>
          {/* Header tagline intentionally omitted — subscription-only product, no lifetime language. */}
        </div>

        <div className="flex items-center justify-center gap-3">
          <Stepdot num={1} label="بياناتك" active={step >= 1} done={step > 1} />
          <div className={`h-0.5 w-12`} style={{ background: step > 1 ? "#FF6A00" : "rgba(255,255,255,0.15)" }} />
          <Stepdot num={2} label="خطتك والدفع" active={step >= 2} done={false} />
        </div>

        {step === 1 && (
          <Card className="p-6 bg-white/[0.03] border-white/10 text-white">
            {attributionBanner && (
              <div className="mb-4 rounded-lg border p-3 text-sm" style={{ background: `${"#FF6A00"}15`, borderColor: `${"#FF6A00"}50` }} data-testid="ck-attribution-banner">
                <div className="flex items-center gap-2 font-semibold" style={{ color: "#FF6A00" }}>
                  <Tag className="h-4 w-4" />
                  <span>عرض خاص من رسالتنا</span>
                </div>
                <div className="text-xs text-white/70 mt-1">
                  {attributionBanner.channel === "whatsapp" ? "وصلتك عبر WhatsApp" : "وصلتك عبر البريد"}
                  {attributionBanner.coupon && (
                    <> · كوبون <b className="font-mono">{attributionBanner.coupon}</b> جاهز للتطبيق</>
                  )}
                </div>
              </div>
            )}
            {prefilled && !attributionBanner && (
              <div className="mb-4 rounded-lg border p-2.5 text-xs flex items-center gap-2" style={{ background: `${"#FF6A00"}15`, borderColor: `${"#FF6A00"}40`, color: "#FF6A00" }} data-testid="ck-prefill-notice">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                <span className="flex-1">مرحبًا بعودتك — لقد ملأنا بياناتك تلقائيًا. راجع القيم وتابع.</span>
                <button
                  type="button"
                  className="hover:underline text-[11px] shrink-0"
                  onClick={() => {
                    setContact({ full_name: "", email: "", whatsapp: "" });
                    setPrefilled(false);
                    try { localStorage.removeItem("ff_checkout_prefill"); } catch {}
                  }}
                  data-testid="ck-clear-prefill"
                >مسح</button>
              </div>
            )}
            <form onSubmit={proceedToStep2} className="space-y-4" data-testid="step1-form" autoComplete="on">
              <div className="space-y-2">
                <Label htmlFor="ck-name-input" className="text-white">الاسم الكامل</Label>
                <Input
                  id="ck-name-input"
                  name="name"
                  autoComplete="name"
                  required minLength={2}
                  value={contact.full_name}
                  onChange={(e) => setContact({ ...contact, full_name: e.target.value })}
                  className="bg-white/[0.05] border-white/15 text-white placeholder-white/30 focus-visible:border-[color:var(--o)]"
                  style={{ "--o": "#FF6A00" }}
                  data-testid="ck-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ck-email-input" className="text-white">البريد الإلكتروني</Label>
                <Input
                  id="ck-email-input"
                  name="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  required
                  value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  className="bg-white/[0.05] border-white/15 text-white placeholder-white/30"
                  data-testid="ck-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ck-whatsapp-input" className="flex items-center gap-2 flex-wrap text-white">
                  <span>رقم واتساب</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-black" style={{ background: "#FF6A00" }}>
                    مهم جداً
                  </span>
                </Label>
                <PhoneInput
                  id="ck-whatsapp-input"
                  name="tel"
                  autoComplete="tel"
                  international
                  defaultCountry={defaultCountry}
                  labels={ar}
                  value={contact.whatsapp}
                  onChange={(v) => setContact({ ...contact, whatsapp: v || "" })}
                  className="phone-input-wrapper"
                  data-testid="ck-whatsapp"
                />
                <p className="text-xs font-semibold" style={{ color: "#FF6A00" }}>
                  سيتم إرسال رابط التطبيق وبيانات الدخول على الواتس آب — تأكد من الرقم قبل المتابعة.
                </p>
              </div>
              <Button
                type="submit"
                disabled={savingLead}
                className="w-full rounded-full gap-2 font-bold text-black hover:brightness-105"
                style={{ background: "#FF6A00", boxShadow: `0 12px 32px -14px #FF6A00` }}
                data-testid="ck-proceed"
              >
                {savingLead ? <Loader2 className="h-4 w-4 animate-spin" /> : <>متابعة <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" /></>}
              </Button>
              <p className="text-xs text-white/60 text-center flex items-center justify-center gap-1 pt-2">
                <Shield className="h-3 w-3" /> بياناتك محفوظة بأمان — الدفع في الخطوة التالية عبر Stripe
              </p>
              {/* Features + trust note removed — checkout copy is fully hardcoded now. */}
            </form>
          </Card>
        )}

        {step === 2 && !clientSecret && (
          <Card className="p-6 space-y-5 bg-white/[0.03] border-white/10 text-white">
            {/* Renewal-mode banner — shows only when the user landed here from
                the SubscriptionExpiredPage. Explains the context so the shopper
                understands why the page opened straight on Step 2. */}
            {isRenewal && (
              <div
                className="rounded-xl border p-4 space-y-1 text-start"
                style={{ background: "rgba(255,106,0,0.08)", borderColor: "rgba(255,106,0,0.35)" }}
                data-testid="ck-renewal-banner"
              >
                <div className="flex items-center gap-2 text-sm font-black" style={{ color: "#FF6A00" }}>
                  <span>🔄</span>
                  <span>تجديد اشتراكك</span>
                </div>
                <p className="text-xs text-white/75 leading-relaxed">
                  {renewalCtx?.last_plan_label
                    ? `أهلاً بعودتك ${(contact.full_name || "").split(" ")[0]} — اخترنا لك ${renewalCtx.last_plan_label} تلقائياً. يمكنك تغييرها من الأسفل إذا رغبت.`
                    : `أهلاً بعودتك ${(contact.full_name || "").split(" ")[0]} — اختر الخطة وأكمل الدفع في ثواني.`}
                </p>
              </div>
            )}

            <div className="flex items-center justify-between text-sm">
              <button type="button" onClick={() => setStep(1)} className="text-white/60 flex items-center gap-1 hover:text-white" data-testid="ck-back-step1">
                <ArrowRight className="h-4 w-4 rtl:-scale-x-100" /> {isRenewal ? "تعديل البيانات" : "عودة للبيانات"}
              </button>
              <span className="text-white/60">مرحبًا {contact.full_name.split(" ")[0]}</span>
            </div>

            <div className="space-y-3">
              <Label className="text-white text-base font-bold">اختر خطتك</Label>
              {showLocalPrice && (
                <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-[11px] md:text-xs text-white/70 flex items-center gap-2" data-testid="ck-currency-hint">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#FF6A00" }} />
                  {chargedInSAR ? (
                    <span>
                      الأسعار معروضة بالعملة المحلية <b className="text-white">({CURRENCY_LABEL[displayCurrency]})</b> — الدفع بالريال السعودي بالسعر المكافئ.
                    </span>
                  ) : (
                    <span>
                      الأسعار والدفع بالعملة المحلية <b className="text-white">({CURRENCY_LABEL[displayCurrency]})</b> — تم التحويل تلقائيًا حسب بلدك.
                    </span>
                  )}
                </div>
              )}
              <div className="grid gap-3" data-testid="plan-picker">
                {(() => {
                  // Read plans from backend; fall back to defaults if the /checkout/plans call hasn't landed yet.
                  // Note: 6mo is hidden for now (see HIDDEN_PUBLIC_PLANS in the backend) — don't render it as a fallback either.
                  const src = plansData?.plans?.length ? plansData.plans : [
                    { id: "1mo",      sar_amount: 147, sub_interval: "month", sub_interval_count: 1, pricing: {} },
                    { id: "12mo",     sar_amount: 547, sub_interval: "year",  sub_interval_count: 1, pricing: {} },
                    { id: "lifetime", sar_amount: 799, sub_interval: null,    sub_interval_count: null, pricing: {} },
                  ];
                  // Display order: 1mo → 6mo → 12mo → lifetime (savings grow with commitment).
                  const RANK = { "1mo": 0, "6mo": 1, "12mo": 2, "lifetime": 3 };
                  const ordered = [...src].sort((a, b) => (RANK[a.id] ?? 99) - (RANK[b.id] ?? 99));
                  const CROSS_MULTIPLIER = 1.5; // strike-through "was" price = base × 1.5
                  return ordered.map((rp) => {
                    const isLifetime = rp.id === "lifetime";
                    const months = isLifetime
                      ? 0  // no monthly rate for lifetime
                      : rp.sub_interval === "month" ? rp.sub_interval_count : 12 * rp.sub_interval_count;
                    const totalSAR = rp.sar_amount;
                    const wasSAR = Math.round(totalSAR * CROSS_MULTIPLIER);
                    const monthlySAR = months > 0 ? Math.round(totalSAR / months) : 0;
                    // The "الأكثر توفير" tag now sits on the lifetime plan — it's the best long-term value.
                    const popular = isLifetime;
                    const totalDisplay = rp.pricing?.[displayCurrency] ?? convertFromSAR(totalSAR, displayCurrency);
                    const wasDisplay = convertFromSAR(wasSAR, displayCurrency);
                    const monthlyDisplay = months > 0 ? Math.round(totalDisplay / months) : 0;
                    const p = {
                      id: rp.id, months,
                      total: totalSAR, was: wasSAR,
                      popular, isLifetime,
                    };
                  const monthly = monthlySAR;
                  const active = plan === p.id;
                  const localTotal = showLocalPrice ? totalDisplay : null;
                  const localWas = showLocalPrice ? wasDisplay : null;
                  const localMonthly = showLocalPrice ? monthlyDisplay : null;
                  // Human-friendly plan title — handles 1 / 6 / 12 months + lifetime cleanly.
                  const planTitle = p.isLifetime
                    ? "مدى الحياة"
                    : p.months === 1
                      ? "اشتراك شهر واحد"
                      : p.months === 6
                        ? "اشتراك 6 أشهر"
                        : p.months === 12
                          ? "اشتراك 12 شهر"
                          : `اشتراك ${p.months} أشهر`;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPlan(p.id)}
                      className="relative w-full text-right rounded-2xl p-5 border-2 transition-all hover:-translate-y-0.5"
                      style={{
                        background: active ? `${"#FF6A00"}15` : "rgba(255,255,255,0.03)",
                        borderColor: active ? "#FF6A00" : "rgba(255,255,255,0.1)",
                        boxShadow: active ? `0 12px 32px -14px #FF6A00` : "none",
                      }}
                      data-testid={`plan-${p.id}`}
                    >
                      {p.popular && (
                        <span className="absolute -top-3 right-5 rounded-full text-[10px] font-black px-3 py-1 text-black shadow-lg" style={{ background: "#FF6A00" }}>
                          الأكثر توفيراً
                        </span>
                      )}
                      <div className="flex items-center gap-4">
                        <span
                          className="w-6 h-6 rounded-full grid place-items-center shrink-0 border-2 transition"
                          style={{
                            background: active ? "#FF6A00" : "transparent",
                            borderColor: active ? "#FF6A00" : "rgba(255,255,255,0.3)",
                          }}
                        >
                          {active && <CheckCircle2 className="w-3.5 h-3.5 text-black" />}
                        </span>
                        <div className="flex-1 flex items-baseline justify-between gap-4 flex-wrap">
                          <div>
                            <div className="text-white font-bold text-lg">
                              {planTitle}
                            </div>
                            <div className="text-xs text-white/50 mt-0.5">
                              {showLocalPrice ? (
                                <>
                                  <span className="line-through opacity-70 me-2">{localWas} {CURRENCY_LABEL[displayCurrency]}</span>
                                  <span>الإجمالي {localTotal} {CURRENCY_LABEL[displayCurrency]}</span>
                                </>
                              ) : (
                                <>
                                  <span className="line-through opacity-70 me-2">{p.was} ريال</span>
                                  <span>الإجمالي {p.total} ريال</span>
                                </>
                              )}
                            </div>
                            {chargedInSAR && (
                              <div className="text-[10px] text-white/35 mt-0.5">
                                ≈ {p.total} ريال سعودي (سعر الدفع)
                              </div>
                            )}
                          </div>
                          <div className="text-left">
                            <div className="flex items-baseline gap-1">
                              <span className="text-3xl md:text-4xl font-black" style={{ color: active ? "#FF6A00" : "#fff" }}>
                                {p.isLifetime
                                  ? (showLocalPrice ? localTotal : p.total)
                                  : (showLocalPrice ? localMonthly : monthly)}
                              </span>
                              <span className="text-xs text-white/60">{CURRENCY_LABEL[displayCurrency]}</span>
                            </div>
                            <div className="text-[10px] text-white/50 font-semibold">
                              {p.isLifetime ? "دفعة واحدة — للأبد" : "شهرياً"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                });
                })()}
              </div>
              <p className="text-[11px] text-white/50 text-center">
                {plan === "lifetime"
                  ? "دفعة واحدة — لا رسوم متكررة، تحصل على كامل المحتوى مدى الحياة."
                  : plan === "1mo"
                    ? "الدفع شهرياً — يجدَّد الاشتراك تلقائياً كل شهر، ويمكنك طلب الإلغاء في أي وقت."
                    : "الدفع مرة واحدة عن كامل مدة الاشتراك. يمكنك طلب الإلغاء في أي وقت."}
              </p>
            </div>

            {/* Bonuses inclusion note */}
            <div className="rounded-2xl border border-white/10 bg-black/40 p-4">
              <div className="text-xs font-bold text-white/70 mb-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#FF6A00" }} />
                يشمل اشتراكك
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  "برنامج المرونة — 3 مستويات",
                  ...(SHOW_HOME_WORKOUT ? ["برنامج التمارين المنزلية بالدامبل"] : []),
                  "مكتبة الوصفات تتجدد أسبوعياً",
                  "استخراج وصفات إنستغرام وتيك توك",
                  "مدرب الذكاء الاصطناعي",
                  "مجتمع فيصل للتمارين",
                  "تواصل خاص مع الكوتش",
                  "الاستخدام على جميع الأجهزة",
                ].map((f, i) => (
                  <div key={f} className="flex items-start gap-2 text-xs text-white/80">
                    <span className="mt-0.5 w-4 h-4 rounded-full grid place-items-center shrink-0 text-black font-black text-[9px]" style={{ background: "#FF6A00" }}>
                      {i + 1}
                    </span>
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-white"><Tag className="h-4 w-4" /> رمز خصم (اختياري)</Label>
              <div className="flex gap-2">
                <Input
                  value={couponCode}
                  onChange={(e) => { setCouponCode(e.target.value); setCouponInfo(null); }}
                  className="bg-white/[0.05] border-white/15 text-white placeholder-white/30"
                  data-testid="ck-coupon"
                />
                <Button type="button" variant="outline" onClick={validateCoupon} disabled={couponBusy || !couponCode} data-testid="ck-coupon-apply">
                  {couponBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "تطبيق"}
                </Button>
              </div>
              {couponInfo && (() => {
                // Show coupon savings in the visitor's display currency (matches
                // the plan-card prices above). Backend already provides per-currency
                // equivalents via `equivalents.original` / `equivalents.final`.
                const localEquiv = (arr) => {
                  if (displayCurrency === "SAR") return null;
                  return (arr || []).find((e) => (e.code || "").toUpperCase() === displayCurrency);
                };
                const origEq = localEquiv(couponInfo.equivalents?.original);
                const finalEq = localEquiv(couponInfo.equivalents?.final);
                const origLocal = origEq ? Number(origEq.amount) : couponInfo.original_sar;
                const finalLocal = finalEq ? Number(finalEq.amount) : couponInfo.final_sar;
                const savedLocal = Math.max(0, origLocal - finalLocal);
                return (
                <div className="rounded-md p-3 space-y-1 border" style={{ background: `${"#FF6A00"}15`, borderColor: `${"#FF6A00"}40` }} data-testid="ck-coupon-applied">
                  <p className="text-sm flex items-center gap-1 font-medium" style={{ color: "#FF6A00" }}>
                    <CheckCircle2 className="h-4 w-4" /> تم تطبيق الخصم
                  </p>
                  <div className="text-xs text-white/70 flex justify-between">
                    <span>السعر الأصلي:</span>
                    <span className="line-through">{origLocal} {displayCurrency}</span>
                  </div>
                  <div className="text-xs flex justify-between" style={{ color: "#FF6A00" }}>
                    <span>وفّرت:</span>
                    <span className="font-semibold">-{savedLocal} {displayCurrency}</span>
                  </div>
                  <div className="text-sm flex justify-between font-semibold pt-1 border-t border-white/15 text-white">
                    <span>السعر النهائي:</span>
                    <span style={{ color: "#FF6A00" }}>{finalLocal} {displayCurrency}</span>
                  </div>
                </div>
                );
              })()}
            </div>

            <Button
              onClick={startPayment}
              disabled={creating}
              className="w-full rounded-full gap-2 font-bold text-black hover:brightness-105"
              style={{ background: "#FF6A00", boxShadow: `0 12px 32px -14px #FF6A00` }}
              data-testid="ck-start-payment"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <>متابعة إلى الدفع <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" /></>}
            </Button>

            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px] text-white/50 pt-2">
              <span className="flex items-center gap-1"><Shield className="h-3 w-3" style={{ color: "#FF6A00" }} /> دفع آمن عبر Stripe</span>
              <span>·</span>
              <span>دخول مباشر بعد الاشتراك</span>
            </div>
          </Card>
        )}

        {step === 2 && clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret, locale: "ar" }}>
            <PaymentStep
              onBack={() => { setClientSecret(null); setChargeInfo(null); }}
              summary={{ plan, config, couponInfo, chargeInfo }}
              contact={contact}
            />
          </Elements>
        )}
      </div>
    </div>
  );
}

function Stepdot({ num, label, active, done }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className="h-9 w-9 rounded-full grid place-items-center text-sm font-bold border"
        style={{
          background: active ? "#FF6A00" : "rgba(255,255,255,0.05)",
          color: active ? "#000" : "rgba(255,255,255,0.5)",
          borderColor: active ? "#FF6A00" : "rgba(255,255,255,0.15)",
        }}
      >
        {done ? <CheckCircle2 className="h-4 w-4" /> : num}
      </div>
      <span className={`text-sm ${active ? "font-bold text-white" : "text-white/50"}`}>{label}</span>
    </div>
  );
}

function PlanOption({ active, onClick, title, priceSar, currency, equivalents, hint, intervalAr, testid }) {
  // Show only 3 most relevant equivalents by default to keep layout clean
  const primary = (equivalents || []).slice(0, 3);
  const secondary = (equivalents || []).slice(3);
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`w-full text-start p-4 rounded-xl border-2 transition-colors ${active ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-display font-semibold">{title}</p>
          <p className="text-xs text-muted-foreground mt-1">{hint}</p>
        </div>
        <div className="text-end shrink-0">
          <div className="font-display font-semibold text-xl">
            {priceSar}<span className="text-sm ms-1 text-muted-foreground">{currency}</span>
          </div>
          {intervalAr && (
            <div className="text-[11px] text-muted-foreground mt-0.5">{intervalAr}</div>
          )}
        </div>
      </div>
      {equivalents.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border/60">
          <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">تقريبًا</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
            {primary.map((e) => (
              <span key={e.code} className="text-muted-foreground">
                <span className="font-mono">{e.amount}</span> <span className="text-[10px]">{e.code}</span>
              </span>
            ))}
            {secondary.length > 0 && (
              <details className="w-full">
                <summary className="text-[10px] text-primary cursor-pointer hover:underline">+ عملات أخرى</summary>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                  {secondary.map((e) => (
                    <span key={e.code} className="text-muted-foreground">
                      <span className="font-mono">{e.amount}</span> <span className="text-[10px]">{e.code}</span>
                    </span>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      )}
    </button>
  );
}

function PaymentStep({ onBack, summary, contact }) {
  const stripe = useStripe();
  const elements = useElements();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setMsg("");
    // Ensure form is fully validated before confirming
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setMsg(submitError.message || "يرجى التحقق من بيانات البطاقة");
      setBusy(false);
      return;
    }
    // Confirm payment with explicit "always" redirect for mobile reliability.
    // Stripe handles 3DS on their hosted page and returns to return_url with status.
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/checkout/success`,
      },
      redirect: "always",
    });
    // If we reach here at all, an immediate client-side error happened (no redirect).
    if (error) {
      setMsg(error.message || "فشل الدفع. يرجى المحاولة مرة أخرى أو استخدام بطاقة أخرى.");
      setBusy(false);
      return;
    }
    // Safeguard — shouldn't reach here with redirect: "always" unless something is off.
    setMsg("حدث خطأ غير متوقع. يرجى إعادة تحميل الصفحة والمحاولة مرة أخرى.");
    setBusy(false);
  };

  const ch = summary.chargeInfo || {};
  // Prefer the actual amount/currency/interval returned by /checkout/create — it reflects
  // the plan_variant the visitor selected (6mo=397, 12mo=547) instead of the config default.
  const displayAmount = ch.display_amount ?? ch.amount_local ?? summary.config.sub_amount;
  const displayCurrency = ch.display_currency ?? ch.currency ?? summary.config.currency;
  const intervalLabel = (() => {
    // Lifetime is a one-time charge — never show a recurring interval label.
    if (summary.plan === "lifetime" || ch.mode === "payment") return "مدى الحياة";
    if (ch.sub_interval === "month" && ch.sub_interval_count === 6) return "كل 6 أشهر";
    if (ch.sub_interval === "year" && ch.sub_interval_count === 1) return "سنويًا";
    if (ch.sub_interval === "month" && ch.sub_interval_count === 1) return "شهريًا";
    return summary.config.sub_interval_ar || "";
  })();
  // When a coupon is applied, backend returns `final_sar` (SAR) + per-currency
  // equivalents. Pick the equivalent that matches the visitor's display currency
  // so we never render a SAR value under a KWD/AED/etc label.
  const finalPrice = (() => {
    if (!summary.couponInfo) return displayAmount;
    if (displayCurrency === "SAR") return summary.couponInfo.final_sar;
    const localEquiv = (summary.couponInfo.equivalents?.final || [])
      .find((e) => (e.code || "").toUpperCase() === displayCurrency);
    if (localEquiv) return Number(localEquiv.amount);
    // Fallback: apply the same discount ratio to the local display amount.
    const orig = summary.couponInfo.original_sar || 1;
    const ratio = summary.couponInfo.final_sar / orig;
    return Math.round(displayAmount * ratio);
  })();

  return (
    <Card className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <button type="button" onClick={onBack} className="text-sm text-muted-foreground flex items-center gap-1 hover:text-foreground" data-testid="ck-back-plan">
          <ArrowRight className="h-4 w-4 rtl:-scale-x-100" /> تغيير الخطة
        </button>
        <div className="text-sm">
          {summary.plan === "lifetime" ? "المبلغ" : "الدفعة الأولى"}: <span className="font-medium">{finalPrice} {displayCurrency}</span>
          <span className="text-muted-foreground text-[11px] ms-2">{intervalLabel}</span>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="step2-form">
        {/* Express Checkout — Apple Pay + Google Pay + Link surface here when the
            buyer's device supports them (Safari on iOS/macOS for Apple Pay,
            Chrome/Android for Google Pay). Stripe hides the whole block if
            none are available, so desktop-Firefox users just see the card form. */}
        <ExpressCheckoutElement
          onConfirm={async (event) => {
            setBusy(true); setMsg("");
            const { error: submitError } = await elements.submit();
            if (submitError) {
              setMsg(submitError.message || "يرجى المحاولة مجددًا");
              setBusy(false);
              return;
            }
            const { error } = await stripe.confirmPayment({
              elements,
              confirmParams: { return_url: `${window.location.origin}/checkout/success` },
              redirect: "always",
            });
            if (error) {
              setMsg(error.message || "فشل الدفع. يرجى المحاولة مرة أخرى.");
              setBusy(false);
            }
          }}
          options={{
            buttonHeight: 48,
            buttonTheme: { applePay: "black", googlePay: "black" },
            layout: { maxColumns: 1, maxRows: 3 },
            paymentMethods: {
              applePay: "always",
              googlePay: "always",
              link: "auto",
            },
          }}
        />
        <div className="relative flex items-center gap-3 text-[11px] text-muted-foreground my-1">
          <div className="h-px flex-1 bg-border" />
          <span>أو ادفع ببطاقة</span>
          <div className="h-px flex-1 bg-border" />
        </div>
        <PaymentElement options={{
          layout: "tabs",
          // Hide Stripe's auto-generated mandate / "By providing your card
          // information..." subscription disclaimer — it's long, in English on
          // SAR flows, and sits ABOVE the pay button where it scares hesitant
          // buyers. We replace it with a shorter, friendlier Arabic version
          // placed under the pay button below.
          terms: {
            card: "never",
            applePay: "never",
            googlePay: "never",
            bancontact: "never",
            ideal: "never",
            sepaDebit: "never",
            sofort: "never",
            auBecsDebit: "never",
            usBankAccount: "never",
            cashapp: "never",
            paypal: "never",
          },
          defaultValues: {
            billingDetails: {
              name: contact?.full_name || "",
              email: contact?.email || "",
              phone: contact?.whatsapp || "",
            },
          },
        }} />
        {msg && <p className="text-sm text-destructive" data-testid="ck-error">{msg}</p>}
        <Button type="submit" disabled={busy || !stripe} className="w-full rounded-full" data-testid="ck-pay">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "ادفع الآن"}
        </Button>
      </form>
    </Card>
  );
}
